import type { ReactNode } from "react";
import { Check, ChevronDown } from "lucide-react";

import { CarrierLogo } from "@/components/common/CarrierLogo";
import { Button } from "@/components/ui/button";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  id?: string;
  value: string;
  options: readonly SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  "aria-label"?: string;
  size?: "sm" | "default" | "xs";
  variant?: "outline" | "ghost" | "pill";
  /** Treat each option's `value` as a carrier SCAC and show logos when the user pref is on. */
  carrierLogos?: boolean;
  /** When false, the menu can grow wider than the trigger (long labels). */
  contentMatchTriggerWidth?: boolean;
  contentClassName?: string;
  /** When false, option labels wrap/show in full instead of truncating. */
  truncateLabels?: boolean;
  /** Optional icon or mark shown before the selected label. */
  leading?: ReactNode;
}

function SelectOptionLabel({
  label,
  carrier,
  showLogos,
  truncate,
}: {
  label: string;
  carrier?: string;
  showLogos: boolean;
  truncate?: boolean;
}) {
  const labelClass = truncate === false ? "whitespace-nowrap" : "truncate";
  if (!showLogos || !carrier) {
    return <span className={labelClass}>{label}</span>;
  }
  return (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <CarrierLogo carrier={carrier} size={16} logoOnly className="shrink-0" />
      <span className={cn("min-w-0", labelClass)}>{label}</span>
    </span>
  );
}

const triggerSizeClass: Record<NonNullable<SelectProps["size"]>, string> = {
  default: "h-9 px-3 text-sm",
  sm: "h-8 px-2 text-xs",
  xs: "h-7 px-2 text-[11px]",
};

/** Glass-themed single select (Radix menu — not a native `<select>`). */
export function Select({
  id,
  value,
  options,
  onChange,
  placeholder = "Select…",
  disabled,
  className,
  triggerClassName,
  "aria-label": ariaLabel,
  size = "default",
  variant = "outline",
  carrierLogos = false,
  contentMatchTriggerWidth = true,
  contentClassName,
  truncateLabels = true,
  leading,
}: SelectProps) {
  const { preferences } = useUserPreferences();
  const showLogos = carrierLogos && preferences.showCarrierLogos;
  const selected = options.find((option) => option.value === value);
  const label = selected?.label ?? placeholder;
  const selectedLabel: ReactNode =
    selected && showLogos ? (
      <SelectOptionLabel
        label={selected.label}
        carrier={selected.value}
        showLogos
        truncate={truncateLabels}
      />
    ) : (
      label
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          id={id}
          type="button"
          variant={
            variant === "pill" ? "pill" : variant === "ghost" ? "ghost" : "outline"
          }
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            "w-full justify-between font-normal",
            variant === "pill" ? "rounded-full" : "rounded-md",
            triggerSizeClass[size],
            variant === "outline" && "border-input shadow-sm",
            !selected && "text-muted-foreground",
            className,
            triggerClassName,
          )}
        >
          <span className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden text-left">
            {leading ? (
              <span className="shrink-0 text-muted-foreground" aria-hidden>
                {leading}
              </span>
            ) : null}
            {typeof selectedLabel === "string" ? (
              <span className={truncateLabels ? "truncate" : "whitespace-nowrap"}>
                {selectedLabel}
              </span>
            ) : (
              selectedLabel
            )}
          </span>
          <ChevronDown
            className={cn(
              "shrink-0 text-muted-foreground",
              size === "xs" ? "size-3" : "size-4",
            )}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={cn(
          "max-h-60 overflow-y-auto",
          contentMatchTriggerWidth
            ? "w-[var(--radix-dropdown-menu-trigger-width)]"
            : "min-w-[var(--radix-dropdown-menu-trigger-width)] w-max max-w-[min(22rem,calc(100vw-2rem))]",
          contentClassName,
        )}
      >
        {options.map((option) => {
          const isSelected = option.value === value;
          return (
            <DropdownMenuItem
              key={option.value || `__empty-${option.label}`}
              disabled={option.disabled}
              onSelect={() => onChange(option.value)}
              className={cn("flex items-center gap-2", isSelected && "bg-primary/12")}
            >
              <SelectOptionLabel
                label={option.label}
                carrier={carrierLogos ? option.value : undefined}
                showLogos={showLogos}
                truncate={truncateLabels}
              />
              {isSelected && (
                <Check className="size-3.5 shrink-0 text-primary" aria-hidden />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
